import { ReactComponent as DetailsIcon } from '@/assets/icons/details.svg'
import DivisorLine from '@/components/DivisorLine'
import RenderIf from '@/components/RenderIf'
import { handleServiceDetailsStatusTag } from '@/utils/statusTags'
import {
  ActionList,
  Box,
  Breadcrumbs,
  Button,
  Table,
  Tag,
  Text,
  Tooltip,
} from '@conexasaude/conexa-hero'
import { Information as InformationIcon } from '@conexasaude/icons'
import { FunctionComponent, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { AuthorizationData } from './components/AuthorizationData/AuthorizationData.component'
import ChargesAndOutcomesModal from './components/ChargesAndOutcomesModal'
import PaymentReverseModal from './components/PaymentReverseModal'
import RefundDetailsModal from './components/RefundDetails/RefundDetailsModal.component'
import ViewOrAttachFilesModal from './components/ViewOrAttachFilesModal'
import {
  appointmentHistoryBreadcrumbItems,
  breadcrumbItems,
  headersChargeAttempt,
  headersOutgoingCall,
  headersReschedule,
  headersServiceDetails,
  ServiceDetailsModal,
} from './ServiceDetails.constants'
import { useServiceDetails } from './ServiceDetails.hook'
import useStyles from './ServiceDetails.styles'

const ServiceDetails: FunctionComponent = () => {
  const styles = useStyles()
  const {
    redirectToReferralDetails,
    modalOpen,
    serviceDetailsList,
    collectionAttemptsList,
    showModal,
    handleNote,
    hasNote,
    titleDetailsOrRefund,
    handleHasPayment,
    serviceId,
    hasPayment,
    isCompleted,
    isNotPending,
    hasRefund,
    hasCancelCategory,
    cancelCategoryLabel,
    handleIconArrow,
    buttonPressed,
    isAppointmentHistoryRoute,
  } = useServiceDetails()

  const shouldShowPBM = useMemo(
    () => ['success', 'error'].includes(serviceDetailsList?.pbmStatus),
    [serviceDetailsList?.pbmStatus],
  )

  const pbmTagVariant = useMemo(
    () => (serviceDetailsList?.pbmStatus === 'success' ? 'success' : 'error'),
    [serviceDetailsList?.pbmStatus],
  )

  const invoiceFields = useMemo(
    () => [
      {
        label: 'RPS',
        value: serviceDetailsList?.patientPayment?.invoiceNumber,
      },
      { label: 'Série', value: serviceDetailsList?.patientPayment?.series },
      {
        label: 'Status de emissão',
        value: serviceDetailsList?.patientPayment?.issuanceStatus,
      },
      { label: 'Filial', value: serviceDetailsList?.patientPayment?.branch },
    ],
    [serviceDetailsList?.patientPayment],
  )

  return (
    <div>
      <Breadcrumbs.Root>
        {(isAppointmentHistoryRoute
          ? appointmentHistoryBreadcrumbItems
          : breadcrumbItems
        )?.map(item => {
          return (
            <Breadcrumbs.Item key={item?.title} active={item?.active}>
              <Link
                to={
                  isAppointmentHistoryRoute
                    ? `/atendimento/historico-atendimentos`
                    : `/usuario/pacientes/atendimentos/${serviceDetailsList?.patientId}`
                }
              >
                {item?.title}
              </Link>
            </Breadcrumbs.Item>
          )
        })}
      </Breadcrumbs.Root>

      <DivisorLine />
      <Table.Root>
        <Table.Heading>
          <Table.Row>
            {headersServiceDetails?.map(header => (
              <Table.Head key={header}>{header}</Table.Head>
            ))}
          </Table.Row>
        </Table.Heading>
        <Table.Body>
          <Table.Row key={serviceDetailsList?.patientId}>
            <Table.Cell>
              <Link
                to={`/usuario/pacientes/atendimentos/${serviceDetailsList?.patientId}`}
                css={styles.patientLink}
              >
                {serviceDetailsList?.patientId}
              </Link>
            </Table.Cell>
            <Table.Cell>{serviceDetailsList?.patientName}</Table.Cell>
            <Table.Cell>{serviceDetailsList?.cpf}</Table.Cell>
            <Table.Cell>{serviceDetailsList?.phone}</Table.Cell>
            <Table.Cell>{serviceDetailsList?.clinic}</Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table.Root>

      <div css={styles.titleWrapperButton}>
        <Text variant="headingSmall" css={styles.titleWrapper}>
          Detalhes do atendimento
        </Text>
        <RenderIf condition={serviceDetailsList?.status !== 'Cancelado'}>
          <ActionList width={200}>
            <ActionList.Target>
              <Button
                onClick={handleIconArrow}
                rightIcon={buttonPressed ? 'arrow-up' : 'arrow-down'}
              >
                Ações
              </Button>
            </ActionList.Target>
            <ActionList.Dropdown>
              <RenderIf condition={isCompleted && hasPayment}>
                <ActionList.Item onClick={() => handleNote(hasNote)}>
                  {hasNote ? 'Baixar nota fiscal' : 'Gerar nota fiscal'}
                </ActionList.Item>
              </RenderIf>
              <RenderIf
                condition={
                  (isNotPending && hasRefund) || (isNotPending && hasPayment)
                }
              >
                <ActionList.Item onClick={() => showModal(handleHasPayment())}>
                  {titleDetailsOrRefund}
                </ActionList.Item>
              </RenderIf>
              <ActionList.Item
                onClick={() =>
                  showModal(ServiceDetailsModal.VIEW_OR_ATTACH_FILES)
                }
              >
                Visualizar ou anexar arquivos
              </ActionList.Item>
            </ActionList.Dropdown>
          </ActionList>
        </RenderIf>
      </div>

      <Box>
        <div css={styles.containerDetails}>
          <div>
            <Text>Id do atendimento</Text>
            <div>
              <Text>{serviceDetailsList?.serviceId || '-'}</Text>
            </div>
          </div>
          <div>
            <Text>Status</Text>
            <div>
              <Tag
                variant={handleServiceDetailsStatusTag(
                  serviceDetailsList?.status,
                )}
              >
                {serviceDetailsList?.status}
              </Tag>
            </div>
          </div>
          <div>
            <Text>Profissional responsável</Text>
            <Text renderAs="p" variant="paragraphSmall">
              {serviceDetailsList?.responsibleProfessionalName}
            </Text>
          </div>
          <div>
            <Text>Especialidade</Text>
            <Text renderAs="p" variant="paragraphSmall">
              {serviceDetailsList?.specialty}
            </Text>
          </div>
          <div>
            <Text>Perfil do usuário da criação</Text>
            <Text renderAs="p" variant="paragraphSmall">
              {serviceDetailsList?.userProfileCreationDescription}
            </Text>
          </div>
          <div>
            <Text>Nome do usuário da criação</Text>
            <Text renderAs="p" variant="paragraphSmall">
              {serviceDetailsList?.createUserName}
            </Text>
          </div>
          <div>
            <Text>Data de criação</Text>
            <Text renderAs="p" variant="paragraphSmall">
              {serviceDetailsList?.createDateHour}
            </Text>
          </div>
          <div>
            <Text>Origem da criação</Text>
            <Text renderAs="p" variant="paragraphSmall">
              {serviceDetailsList?.createOrigin}
            </Text>
          </div>
          <RenderIf condition={serviceDetailsList?.status === 'Pendente'}>
            <div>
              <Text>Consulta confirmada</Text>
              <Text renderAs="p" variant="paragraphSmall">
                {serviceDetailsList?.flagConfirmedAppintment}
              </Text>
            </div>
          </RenderIf>
          <RenderIf condition={serviceDetailsList?.status === 'Concluido'}>
            <div>
              <Text>Desfecho</Text>
              <Text renderAs="p" variant="paragraphSmall">
                {serviceDetailsList?.outcomes}
              </Text>
            </div>
          </RenderIf>
          <RenderIf condition={serviceDetailsList?.status === 'Cancelado'}>
            <div>
              <Text>Data do cancelamento</Text>
              <Text renderAs="p" variant="paragraphSmall">
                {serviceDetailsList?.cancelDate}
              </Text>
            </div>
            <div>
              <Text>Perfil do usuário do cancelamento</Text>
              <Text renderAs="p" variant="paragraphSmall">
                {serviceDetailsList?.cancelProfileUser}
              </Text>
            </div>
            <RenderIf condition={hasCancelCategory}>
              <div>
                <Text>Categoria do cancelamento</Text>
                <Text renderAs="p" variant="paragraphSmall">
                  {cancelCategoryLabel}
                </Text>
              </div>
            </RenderIf>
            <RenderIf condition={!hasCancelCategory}>
              <div>
                <Text>Motivo do cancelamento</Text>
                <Text renderAs="p" variant="paragraphSmall">
                  {serviceDetailsList?.cancelReason}
                </Text>
              </div>
            </RenderIf>
            <div>
              <Text>Nome do usuário do cancelamento</Text>
              <Text renderAs="p" variant="paragraphSmall">
                {serviceDetailsList?.cancelNameUser}
              </Text>
            </div>
          </RenderIf>
          <RenderIf condition={shouldShowPBM}>
            <div>
              <div css={styles.tooltip}>
                <Text>Integrado PBM</Text>
                <Tooltip
                  label="Programa de Benefícios e Medicamentos"
                  multiline
                  position="right"
                  width={250}
                  withArrow
                >
                  <div>
                    <InformationIcon />
                  </div>
                </Tooltip>
              </div>
              <div>
                <Tag variant={pbmTagVariant}>
                  {serviceDetailsList?.pbmStatus === 'success'
                    ? 'Integrado'
                    : 'Não Integrado'}
                </Tag>
              </div>
            </div>

            <div>
              <Text>Parceiro PBM</Text>
              <Text renderAs="p" variant="paragraphSmall">
                {serviceDetailsList?.pbmPartner}
              </Text>
            </div>
          </RenderIf>
        </div>
      </Box>

      <AuthorizationData idAuthorizer={serviceDetailsList?.idAuthorizer} />

      <RenderIf condition={hasPayment}>
        <Text variant="headingSmall" css={styles.titleWrapper}>
          Informações de pagamento
        </Text>
        <Box>
          <div css={styles.boxContainer}>
            <div>
              <Text>Status do pagamento</Text>
              <Text renderAs="p" variant="paragraphSmall">
                {serviceDetailsList?.patientPayment?.situationPayment}
              </Text>
            </div>
            <div>
              <Text>Valor da consulta</Text>
              <Text renderAs="p" variant="paragraphSmall">
                {serviceDetailsList?.patientPayment?.amountPaid}
              </Text>
            </div>
            <div>
              <Text>Cartão utilizado</Text>
              <Text renderAs="p" variant="paragraphSmall">
                Final {serviceDetailsList?.patientPayment?.endCard}
              </Text>
            </div>
            <div>
              <Text>Bandeira do cartão</Text>
              <Text renderAs="p" variant="paragraphSmall">
                {serviceDetailsList?.patientPayment?.flagCard}
              </Text>
            </div>
            <div>
              <Text>Id Transação</Text>
              <Text renderAs="p" variant="paragraphSmall">
                {serviceDetailsList?.patientPayment?.transactionId || '-'}
              </Text>
            </div>
            <div>
              <Text>Id NFE</Text>
              <Text renderAs="p" variant="paragraphSmall">
                {serviceDetailsList?.patientPayment?.nfeId || '-'}
              </Text>
            </div>
            {invoiceFields.map(field => (
              <div key={field.label}>
                <Text>{field.label}</Text>
                <Text renderAs="p" variant="paragraphSmall">
                  {field.value || '-'}
                </Text>
              </div>
            ))}
          </div>
        </Box>
      </RenderIf>

      <Text variant="headingSmall" css={styles.titleWrapper}>
        Chamadas efetuadas
      </Text>
      <Table.Root>
        <Table.Heading>
          <Table.Row>
            {headersOutgoingCall?.map(header => (
              <Table.Head key={header}>{header}</Table.Head>
            ))}
          </Table.Row>
        </Table.Heading>
        <Table.Body>
          {serviceDetailsList?.calls?.map(item => (
            <Table.Row key={item?.callsId}>
              <Table.Cell>{item?.startDateTime}</Table.Cell>
              <Table.Cell>{item?.endDateTime}</Table.Cell>
              <Table.Cell>{item?.typeMeeting}</Table.Cell>
              <Table.Cell>{item?.duration} min</Table.Cell>
              <Table.Cell>
                <DetailsIcon
                  css={styles.detailsAnchor}
                  onClick={() => redirectToReferralDetails(item, serviceId)}
                />
              </Table.Cell>
            </Table.Row>
          ))}

          <Table.NotFound
            columns={6}
            isShow={!serviceDetailsList?.calls?.length}
            label="Sem histórico para exibir"
            variant="small"
          />
        </Table.Body>
      </Table.Root>

      <Text variant="headingSmall" css={styles.titleWrapper}>
        Tentativas de cobrança ou desfecho
      </Text>
      <Table.Root css={styles.tableWrapper}>
        <Table.Heading>
          <Table.Row>
            {headersChargeAttempt?.map(header => (
              <Table.Head key={header}>{header}</Table.Head>
            ))}
          </Table.Row>
        </Table.Heading>
        <Table.Body>
          {collectionAttemptsList?.map(item => (
            <Table.Row key={item?.collectionId}>
              <Table.Cell>{item?.transactionId}</Table.Cell>
              <Table.Cell>{item?.date}</Table.Cell>
              <Table.Cell>{item?.actionPerformed}</Table.Cell>
              <Table.Cell>
                <Tag variant={handleServiceDetailsStatusTag(item?.status)}>
                  {item?.status}
                </Tag>
              </Table.Cell>
              <Table.Cell>
                {item?.userName}
                <Text renderAs="p" variant="microCopy">
                  ID: {item?.userId}
                </Text>
              </Table.Cell>
            </Table.Row>
          ))}

          <Table.NotFound
            columns={6}
            isShow={!collectionAttemptsList?.length}
            label="Sem resultados para exibir"
            variant="small"
          />
        </Table.Body>
      </Table.Root>

      <Text variant="headingSmall" css={styles.titleWrapper}>
        Histórico de reagendamentos
      </Text>
      <Table.Root css={styles.tableWrapper}>
        <Table.Heading>
          <Table.Row>
            {headersReschedule?.map(header => (
              <Table.Head key={header}>{header}</Table.Head>
            ))}
          </Table.Row>
        </Table.Heading>
        <Table.Body>
          {serviceDetailsList?.reschedules?.map((item, index) => (
            <Table.Row key={index}>
              <Table.Cell>{item?.originalDateTime}</Table.Cell>
              <Table.Cell>{item?.rescheduledDateTime}</Table.Cell>
              <Table.Cell>{item?.rescheduleDateTime}</Table.Cell>
              <Table.Cell>{item?.responsibleUserProfile}</Table.Cell>
              <Table.Cell>{item?.responsibleUserId}</Table.Cell>
              <Table.Cell>{item?.responsibleUserName}</Table.Cell>
              <Table.Cell>{item?.responsibleUserIdProfessional}</Table.Cell>
              <Table.Cell>{item?.responsibleUserNameProfessional}</Table.Cell>
            </Table.Row>
          ))}

          <Table.NotFound
            columns={6}
            isShow={!serviceDetailsList?.reschedules?.length}
            label="Sem resultados para exibir"
            variant="small"
          />
        </Table.Body>
      </Table.Root>
      <RenderIf condition={modalOpen === ServiceDetailsModal.PAYMENT_REVERSE}>
        <PaymentReverseModal />
      </RenderIf>
      <RenderIf condition={modalOpen === ServiceDetailsModal.REFUND_DETAILS}>
        <RefundDetailsModal />
      </RenderIf>
      <RenderIf condition={modalOpen === ServiceDetailsModal.CHARGES_OUTCOMES}>
        <ChargesAndOutcomesModal />
      </RenderIf>
      <RenderIf
        condition={modalOpen === ServiceDetailsModal.VIEW_OR_ATTACH_FILES}
      >
        <ViewOrAttachFilesModal
          attendanceSelectedAppointment={serviceDetailsList?.serviceId}
        />
      </RenderIf>
    </div>
  )
}

export default ServiceDetails
